import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The drawing core of a signature pad: the pointer and transform arithmetic, and nothing else.
 *
 * WHY A HOOK AND NOT A COMPONENT. There were already five signature canvases in this app before
 * this file existed, and every one of them had solved — or failed to solve — the same four problems
 * independently. The version this is lifted from (the public inspection sheet) is the one that
 * solved all four, because it is the one filled in on a phone in a yard where the failures are
 * unrecoverable. Sharing it as a hook rather than a component means the `<canvas>` element stays in
 * each page's own markup, so a page can label, size and place it however it needs while the
 * arithmetic below has exactly one home.
 *
 * THE FOUR PROBLEMS, and why each line that solves one is not optional:
 *
 *   1. DEVICE PIXEL RATIO. A canvas sized once at a fixed scale desyncs the ink from the finger the
 *      moment the phone is rotated. Capped at 2x: nothing above that is visible in a mark made of
 *      3px strokes, and a 4x tablet would quadruple the base64 that ends up in the payload.
 *   2. REPAINT ON RESIZE. Re-fitting the backing store clears it. iOS collapsing its address bar is
 *      enough to fire a resize, so a pad that only re-fits loses the signature to a scroll.
 *   3. THE SECOND FINGER. Signing one-handed means the other hand is holding the phone. A resting
 *      palm delivers its own pointer events, and an unguarded handler lets it draw a line from the
 *      signature to wherever the palm landed — or, worse, end the stroke the signing finger is still
 *      drawing, with no way to restart it because that finger never lifted.
 *   4. CLEARING THROUGH A TRANSFORM. `clearRect`'s arguments go through the current transform;
 *      `canvas.width`/`height` do not. Passing one to the other clears only `dpr` of each axis, so
 *      wherever devicePixelRatio is below 1 the old mark survives underneath a caption reading
 *      "not signed yet", and the next stroke commits both.
 *
 * COMMITS ON EVERY STROKE, and there is deliberately no Save step. A Save button is the thing that
 * loses a signature: draw, submit, and be told an answer is still needed for a box that visibly has
 * one in it.
 *
 * @param {string}   value     the committed signature, as a PNG data URL ('' when unsigned)
 * @param {function} onChange  called with the new data URL on every stroke, and '' on clear
 * @returns {{canvasRef, handlers, clear, touched, signed}} spread `handlers` onto the <canvas>
 */
export default function useSignatureCanvas({ value, onChange }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  // The committed value, readable from a callback that must not be rebuilt on every stroke.
  const latest = useRef(value)
  latest.current = value
  const [touched, setTouched] = useState(false)

  // Re-fit the backing store to the element's real size and pixel density, then paint the signature
  // back onto it.
  const configure = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    if (!rect.width) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.round(rect.width * dpr)
    canvas.height = Math.round(rect.height * dpr)
    const ctx = canvas.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)   // reset + scale in one call, so resizes don't compound
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    // Heavier and darker than a desktop pad's 2px: this one is read at arm's length, in direct sun,
    // through a screen protector that has been in a truck for two years.
    ctx.lineWidth = 3
    ctx.strokeStyle = '#0f172a'
    // The canvas is left TRANSPARENT, never filled white, so the PNG composes over a PDF page the
    // same way the in-app pad's output does.
    if (latest.current) {
      const img = new Image()
      img.onload = () => {
        // ONE scale for both axes. The pad's height is pinned and its width follows the column, so a
        // rotation only ever changes the width — filling the new box with the old bitmap's corners
        // would print a mark half again as wide as the one the person drew, and the next touch
        // commits that stretched PNG onto a signed document.
        const w = img.width / dpr
        const h = img.height / dpr
        const scale = Math.min(rect.width / w, rect.height / h)
        ctx.drawImage(img, 0, 0, w * scale, h * scale)
      }
      img.src = latest.current
    }
  }, [])

  useEffect(() => {
    configure()
    const canvas = canvasRef.current
    if (!canvas) return undefined
    if (typeof ResizeObserver === 'undefined') {
      // No ResizeObserver: fall back to the window, which catches a rotation and a zoom change.
      // Without this the backing store is fitted once, at mount, and never again.
      const onResize = () => configure()
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }
    const ro = new ResizeObserver(() => configure())
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [configure])

  /**
   * Does the backing store still match the element it is displayed in?
   *
   * IF IT DOES NOT, EVERY COORDINATE IS WRONG — and wrong in a way that looks like the pad is
   * broken rather than mis-sized. `at()` returns CSS pixels and the context is scaled by `dpr`, so
   * the two agree only while `canvas.width === rect.width * dpr`. Once they diverge the browser
   * stretches the bitmap to fill the element, and the ink lands somewhere other than the finger,
   * stretched by exactly the ratio.
   *
   * Divergence has several causes and they are not all observable: a layout change between the
   * effect and the first stroke, a browser zoom (which moves devicePixelRatio), a ResizeObserver
   * callback that has not been delivered yet, or a page that grew a scrollbar. Rather than chase
   * each one, this is checked at the moment it matters.
   */
  const fitsItsElement = () => {
    const canvas = canvasRef.current
    if (!canvas) return true
    const rect = canvas.getBoundingClientRect()
    if (!rect.width) return true
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    return canvas.width === Math.round(rect.width * dpr)
      && canvas.height === Math.round(rect.height * dpr)
  }

  const at = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  // The primary pointer only — see problem 3 above.
  const down = (e) => {
    if (!e.isPrimary) return
    // BEFORE THE FIRST POINT OF EVERY STROKE, not only when something told us the size changed.
    // A re-fit is cheap and idempotent; ink that does not follow the finger is unrecoverable and
    // looks like the whole pad is broken.
    if (!fitsItsElement()) configure()
    const canvas = canvasRef.current
    // Pointer capture, so a stroke that runs off the edge of the pad keeps drawing instead of
    // stopping half way through a name. Pointer events are also why one set of handlers serves a
    // finger, a stylus and a mouse.
    try { canvas.setPointerCapture(e.pointerId) } catch (err) { /* uncaptured still draws */ }
    drawing.current = true
    setTouched(true)
    const ctx = canvas.getContext('2d')
    const { x, y } = at(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    // A dot is a mark. Without this line an initial, or the full stop after one, draws nothing.
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const move = (e) => {
    if (!drawing.current || !e.isPrimary) return
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = at(e)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  // Bound to pointerup, pointercancel AND pointerleave. A touch pointer is implicitly captured to
  // the element it landed on, so a steadying palm delivers its own end event here; unguarded, that
  // stray contact drops `drawing` while the signing finger is still on the glass and no pointerdown
  // can restart the stroke, because the finger never lifted. The primary's own end events all carry
  // isPrimary === true, so nothing real is lost.
  const up = (e) => {
    if (e && !e.isPrimary) return
    if (!drawing.current) return
    drawing.current = false
    onChange(canvasRef.current.toDataURL('image/png'))
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    // Cleared in DEVICE pixels, which means dropping configure's scale(dpr) first — see problem 4.
    // `configure()` cannot be reused here: `latest.current` still holds the old value at this point,
    // so it would faithfully repaint the signature that was just cleared.
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.restore()
    drawing.current = false
    setTouched(false)
    onChange('')     // '' rather than undefined: every server here reads both as unanswered, and ''
                     // is what the in-app pad's onClear writes.
  }

  return {
    canvasRef,
    // Spread onto the <canvas>. All four end events, deliberately.
    handlers: {
      onPointerDown: down,
      onPointerMove: move,
      onPointerUp: up,
      onPointerCancel: up,
      onPointerLeave: up,
    },
    clear,
    touched,
    signed: !!value,
  }
}

/**
 * The style a signature canvas must carry, wherever it is drawn.
 *
 * `touchAction: 'none'` belongs on the CANVAS and nowhere else. Without it the browser claims a
 * stroke as a scroll gesture and the ink stops the instant the page moves; with it any higher up the
 * tree, the person is trapped on a page they cannot scroll. (A global preventDefault would not work
 * anyway — React 18 registers touchstart/touchmove/wheel passively at the root.)
 */
export const SIGNATURE_CANVAS_STYLE = {
  display: 'block',
  width: '100%',
  touchAction: 'none',
  cursor: 'crosshair',
}
